import { City, CellKind, type BuildingStyle } from '@/world/city';
import { Noise } from '@/utils/noise';
import { clamp, lerp } from '@/utils/math';
import { WORLD } from '@/config';

/**
 * Procedural layout for Palimpsest.
 *
 * The grammar is deliberately simple and legible: a regular avenue lattice, blocks
 * between the avenues, and occasional plazas. Legibility matters because the whole
 * point is that the player will later *feel* the layout deform toward their habits;
 * if the base city were noisy chaos, that signal would be lost.
 *
 * Where earlier iterations spawned a single box per block, each block now spawns a
 * small **composition** of logical `Building`s — a podium under a shaft, a ring of
 * court bars around a walkable yard, a run of terraced steps. Every part is a real
 * `Building` (its own footprint / height / style), so it inherits an instance slot,
 * growth animation and player collision for free; no sub-part machinery lives
 * anywhere else. All parts are ground-anchored boxes (geometry y ∈ [0,1] scaled by
 * height in the shader), so "setbacks" are concentric boxes of different heights and
 * "stairs" are adjacent boxes of rising heights.
 *
 * Heights come from layered noise so the skyline is spatially coherent (a district
 * of towers, a district of low courts) rather than salt-and-pepper. The initial
 * city is deliberately LOW-RISE (≤ 18m), leaving vertical headroom for the evolution
 * engine to express "verticality" dramatically later.
 *
 * Determinism: every "random" choice flows through `city.rng` (or the seeded style /
 * height `Noise` fields forked from it). We never touch `Math.random()`.
 */

const AVENUE_EVERY = 4;

/** No building the generator makes exceeds this; evolution raises heights later. */
const LOW_RISE_CAP = 18;

export class CityGenerator {
  private heightNoise: Noise;
  private styleNoise: Noise;
  /** True only while generateInitial() runs — see addPart's seedHeight. */
  private seeding = false;

  constructor(private city: City) {
    this.heightNoise = new Noise(city.rng.fork(11));
    this.styleNoise = new Noise(city.rng.fork(29));
  }

  /** Build the seed city out to the initial radius. */
  generateInitial(): void {
    this.seeding = true;
    const r = this.city.builtRadius;
    const c = Math.round(this.city.centre);
    for (let cz = c - r; cz <= c + r; cz++) {
      for (let cx = c - r; cx <= c + r; cx++) {
        if (!this.city.inBounds(cx, cz)) continue;
        if (this.city.ringOf(cx, cz) > r) continue;
        this.populateCell(cx, cz);
      }
    }
    // Reserve a small plaza at the exact centre — the player's birthplace, an
    // intentional pool of stillness the city later grows around.
    const cc = c;
    for (let dz = -1; dz <= 1; dz++)
      for (let dx = -1; dx <= 1; dx++) this.clearToPlaza(cc + dx, cc + dz);

    // HARD REQUIREMENT: climbable route from minute one. Force a few terrace
    // compositions just outside the central plaza so a roof is always reachable.
    this.placeCentralTerraces();
    this.seeding = false;
  }

  /** Activate the next `rings` frontier rings and populate them. Returns count added. */
  growFrontier(rings = 1): number {
    let added = 0;
    for (let step = 0; step < rings; step++) {
      const target = this.city.builtRadius + 1;
      if (target > this.maxRing) break;
      const c = Math.round(this.city.centre);
      for (let cz = c - target; cz <= c + target; cz++) {
        for (let cx = c - target; cx <= c + target; cx++) {
          if (!this.city.inBounds(cx, cz)) continue;
          if (this.city.ringOf(cx, cz) !== target) continue;
          if (this.city.kindAt(cx, cz) !== CellKind.Void) continue;
          if (this.populateCell(cx, cz)) added++;
        }
      }
      this.city.builtRadius = target as number;
    }
    return added;
  }

  private get maxRing(): number {
    // Bounded by both the design limit and the physical grid.
    return Math.min(WORLD.maxRadius, Math.floor((this.city.grid - 1) / 2) - 1);
  }

  private clearToPlaza(cx: number, cz: number): void {
    this.city.setKind(cx, cz, CellKind.Plaza);
    this.clearCellBuildings(cx, cz);
  }

  /** Remove any buildings already parented to this cell (used before re-composing). */
  private clearCellBuildings(cx: number, cz: number): void {
    for (let i = this.city.buildings.length - 1; i >= 0; i--) {
      if (this.city.buildings[i].cx === cx && this.city.buildings[i].cz === cz) {
        this.city.buildings.splice(i, 1);
      }
    }
  }

  /** Decide a cell's kind and spawn its contents. Returns true if a building was added. */
  private populateCell(cx: number, cz: number): boolean {
    const isAvenue = cx % AVENUE_EVERY === 0 || cz % AVENUE_EVERY === 0;
    if (isAvenue) {
      this.city.setKind(cx, cz, CellKind.Street);
      return false;
    }
    // Plaza chance from noise, biased slightly higher near centre for breathing room.
    const openness = this.styleNoise.fbm2D(cx * 0.18, cz * 0.18, 3);
    if (openness > 0.42) {
      this.city.setKind(cx, cz, CellKind.Plaza);
      return false;
    }
    this.city.setKind(cx, cz, CellKind.Block);
    this.spawnComposition(cx, cz);
    return true;
  }

  /* ------------------------------------------------------------------ */
  /* Composition dispatch                                                */
  /* ------------------------------------------------------------------ */

  private spawnComposition(cx: number, cz: number): void {
    const { x, z } = this.city.cellToWorld(cx, cz);
    // Coherent height field in [0,1]; each style maps it to its own band.
    const h01 = clamp((this.heightNoise.fbm2D(cx * 0.11, cz * 0.11, 4) + 1) * 0.5, 0, 1);
    const style = this.pickStyle(cx, cz);
    switch (style) {
      case 'tower':
        this.composeTower(cx, cz, x, z, h01);
        break;
      case 'slab':
        this.composeSlab(cx, cz, x, z, h01);
        break;
      case 'court':
        this.composeCourt(cx, cz, x, z, h01);
        break;
      case 'stack':
        this.composeStack(cx, cz, x, z, h01);
        break;
      case 'terrace':
        this.composeTerrace(cx, cz, x, z);
        break;
      default:
        this.composeStack(cx, cz, x, z, h01);
    }
  }

  /**
   * District identity: styles come from a LOW-frequency noise field so common
   * styles cluster into legible neighbourhoods (a quarter of towers, a quarter of
   * courts), plus ~8% wildcard cells that break the pattern up.
   */
  private pickStyle(cx: number, cz: number): BuildingStyle {
    const rng = this.city.rng;
    // ~8% wildcard cells sprinkled through every district.
    if (rng.chance(0.08)) {
      return rng.pick(['tower', 'slab', 'court', 'stack', 'terrace'] as const);
    }
    // Large patches: the low 0.05 frequency makes each district several cells wide.
    const n = (this.styleNoise.fbm2D(cx * 0.05 + 100, cz * 0.05 - 40, 2) + 1) * 0.5;
    if (n > 0.66) return 'tower';
    if (n > 0.46) return 'court';
    if (n > 0.3) return 'slab';
    return 'stack';
  }

  /* ------------------------------------------------------------------ */
  /* Part helper                                                         */
  /* ------------------------------------------------------------------ */

  private addPart(
    cx: number,
    cz: number,
    style: BuildingStyle,
    px: number,
    pz: number,
    w: number,
    d: number,
    h: number,
    yaw = 0,
  ): void {
    const rng = this.city.rng;
    this.city.addBuilding({
      x: px,
      z: pz,
      width: w,
      depth: d,
      yaw,
      height: clamp(h, 1, LOW_RISE_CAP),
      // Buildings born during generateInitial() belong to the seed city and keep
      // their height through the finale's recall; frontier growth exists because
      // of the player and recalls to nothing.
      seedHeight: this.seeding ? clamp(h, 1, LOW_RISE_CAP) : 0,
      tone: rng.next(),
      seed: rng.range(0, 1000),
      style,
      cx,
      cz,
      bornAt: 0,
    });
  }

  /* ------------------------------------------------------------------ */
  /* Compositions                                                        */
  /* ------------------------------------------------------------------ */

  /** tower: broad podium + slender shaft + 40% chance of a slim spire crown. */
  private composeTower(cx: number, cz: number, x: number, z: number, h01: number): void {
    const rng = this.city.rng;
    const cs = this.city.cellSize;

    const podW = cs * rng.range(0.62, 0.72);
    const podD = podW * rng.range(0.9, 1.0);
    const podH = rng.range(3.5, 5.0);
    this.addPart(cx, cz, 'tower', x, z, podW, podD, podH, 0);

    const withSpire = rng.chance(0.4);
    const shaftW = cs * rng.range(0.4, 0.5);
    const shaftD = shaftW * rng.range(0.9, 1.05);
    const jit = cs * 0.03;
    // A crowned tower keeps its shaft a touch lower so the whole stack stays ≤ 18m.
    const shaftTop = withSpire ? lerp(9, 12, h01) : lerp(10, 16, h01);
    this.addPart(
      cx,
      cz,
      'tower',
      x + rng.range(-jit, jit),
      z + rng.range(-jit, jit),
      shaftW,
      shaftD,
      shaftTop,
      0,
    );

    if (withSpire) {
      const spW = rng.range(0.6, 1.2);
      const spH = clamp(shaftTop + rng.range(2.5, 5.0), 0, LOW_RISE_CAP);
      this.addPart(cx, cz, 'spire', x, z, spW, spW, spH, rng.range(-0.2, 0.2));
    }
  }

  /** slab: one long bar plus a thinner parallel bar → strong horizontal reading. */
  private composeSlab(cx: number, cz: number, x: number, z: number, h01: number): void {
    const rng = this.city.rng;
    const cs = this.city.cellSize;

    const longAlongX = rng.chance(0.5);
    const longL = cs * rng.range(0.8, 0.9);
    const shortL = cs * rng.range(0.26, 0.34);
    const h = lerp(6, 10, h01);

    if (longAlongX) this.addPart(cx, cz, 'slab', x, z, longL, shortL, h, 0);
    else this.addPart(cx, cz, 'slab', x, z, shortL, longL, h, 0);

    const dir = rng.chance(0.5) ? 1 : -1;
    const off = cs * rng.range(0.24, 0.32) * dir;
    const short2 = shortL * rng.range(0.5, 0.72);
    const long2 = longL * rng.range(0.85, 0.95);
    const h2 = h * rng.range(0.7, 0.92);
    if (longAlongX) this.addPart(cx, cz, 'slab', x, z + off, long2, short2, h2, 0);
    else this.addPart(cx, cz, 'slab', x + off, z, short2, long2, h2, 0);
  }

  /** court: 3–4 thin perimeter bars around a WALKABLE yard, with one entry gap. */
  private composeCourt(cx: number, cz: number, x: number, z: number, h01: number): void {
    const rng = this.city.rng;
    const cs = this.city.cellSize;

    const h = lerp(4, 6, h01);
    const half = cs * rng.range(0.3, 0.34); // bar centre offset from cell centre
    const thick = cs * rng.range(0.12, 0.16);
    const barLen = cs * rng.range(0.6, 0.68);
    const entry = rng.int(0, 3); // side left open / gapped for entry
    const keepFour = rng.chance(0.5); // 4 bars (gapped entry) vs 3 (open side)

    // Sides: 0 = +z (N), 1 = -z (S), 2 = +x (E), 3 = -x (W).
    for (let side = 0; side < 4; side++) {
      const runsAlongX = side < 2; // N/S bars run along X; E/W along Z.
      const isEntry = side === entry;
      if (isEntry && !keepFour) continue; // 3-bar court: this side is a clean gap.

      // For a kept-but-gapped entry, shorten the bar and shove it to one end so a
      // person-sized opening remains on the other end.
      const len = isEntry ? barLen * 0.5 : barLen;
      let ox = 0;
      let oz = 0;
      if (runsAlongX) {
        oz = side === 0 ? half : -half;
        if (isEntry) ox = (barLen - len) * 0.5; // slide to +x end, gap at -x end
        this.addPart(cx, cz, 'court', x + ox, z + oz, len, thick, h, 0);
      } else {
        ox = side === 2 ? half : -half;
        if (isEntry) oz = (barLen - len) * 0.5;
        this.addPart(cx, cz, 'court', x + ox, z + oz, thick, len, h, 0);
      }
    }
  }

  /** stack: 2–3 overlapping offset boxes with slight yaw jitter → broken massing. */
  private composeStack(cx: number, cz: number, x: number, z: number, h01: number): void {
    const rng = this.city.rng;
    const cs = this.city.cellSize;

    const n = rng.int(2, 3);
    const baseH = lerp(7, 12, h01);
    for (let i = 0; i < n; i++) {
      const w = cs * rng.range(0.44, 0.56);
      const d = cs * rng.range(0.44, 0.56);
      const ox = i === 0 ? 0 : cs * rng.range(-0.16, 0.16);
      const oz = i === 0 ? 0 : cs * rng.range(-0.16, 0.16);
      const h = baseH * rng.range(0.8, 1.05);
      const yaw = rng.range(-0.15, 0.15);
      this.addPart(cx, cz, 'stack', x + ox, z + oz, w, d, h, yaw);
    }
  }

  /**
   * terrace: 3–4 adjacent steps (risers ≤ 1.15m, apex ~3.5–5m) climbing up to a
   * short companion block whose roof is therefore reachable — the city's stairs.
   */
  private composeTerrace(cx: number, cz: number, x: number, z: number): void {
    const rng = this.city.rng;
    const cs = this.city.cellSize;

    const steps = rng.int(3, 4);
    const climbAlongX = rng.chance(0.5);
    const dir = rng.chance(0.5) ? 1 : -1;
    const across = cs * rng.range(0.44, 0.5);
    const climbSpan = cs * 0.54;
    const stepDepth = climbSpan / steps;
    const start = -climbSpan / 2;
    const riser = rng.range(0.95, 1.15); // ≤ 1.15m, below the ~1.4m jump apex.

    for (let i = 0; i < steps; i++) {
      const h = riser * (i + 1); // 0.95 … ~4.6m
      const along = (start + stepDepth * (i + 0.5)) * dir;
      const depth = stepDepth * 1.02;
      if (climbAlongX) this.addPart(cx, cz, 'terrace', x + along, z, depth, across, h, 0);
      else this.addPart(cx, cz, 'terrace', x, z + along, across, depth, h, 0);
    }

    // Companion landing block: its roof sits ~0.4–0.9m above the top step, so it is
    // reachable with a small step-up/jump and gives somewhere to stand.
    const topH = riser * steps;
    const compH = clamp(topH + rng.range(0.4, 0.9), 3.5, 6.0);
    const compDepth = cs * 0.24;
    const compAlong = (start + climbSpan + compDepth * 0.5 - stepDepth * 0.02) * dir;
    if (climbAlongX)
      this.addPart(cx, cz, 'court', x + compAlong, z, compDepth, across * 1.05, compH, 0);
    else this.addPart(cx, cz, 'court', x, z + compAlong, across * 1.05, compDepth, compH, 0);
  }

  /* ------------------------------------------------------------------ */
  /* Guaranteed spawn-adjacent terraces                                  */
  /* ------------------------------------------------------------------ */

  /**
   * Force ≥ 2 (we place up to 3) terrace compositions in rings 2–3 around the
   * central plaza. Candidate offsets are hand-picked to avoid avenue cells (whose
   * indices are multiples of AVENUE_EVERY) so the stairs land on real blocks
   * immediately outside the spawn plaza.
   */
  private placeCentralTerraces(): void {
    const c = Math.round(this.city.centre);
    const offsets: Array<[number, number]> = [
      [2, 1],
      [-2, -1],
      [1, 2],
      [-1, -2],
      [2, -1],
      [-2, 1],
      [3, 1],
      [-3, -1],
      [1, 3],
      [-1, 3],
    ];
    let placed = 0;
    for (const [dx, dz] of offsets) {
      if (placed >= 3) break;
      const cx = c + dx;
      const cz = c + dz;
      if (!this.city.inBounds(cx, cz)) continue;
      // Skip avenue cells so we never wall off a through-street.
      if (cx % AVENUE_EVERY === 0 || cz % AVENUE_EVERY === 0) continue;
      const ring = this.city.ringOf(cx, cz);
      if (ring < 2 || ring > 3) continue;

      this.clearCellBuildings(cx, cz);
      this.city.setKind(cx, cz, CellKind.Block);
      const { x, z } = this.city.cellToWorld(cx, cz);
      this.composeTerrace(cx, cz, x, z);
      placed++;
    }
  }
}
